// src/QRmessaging/QRTemplateManager.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './QRclient.css';

const QRTemplateManager = ({ language, user, SERVER_ROOT }) => {
  const [templates, setTemplates] = useState([]);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('my-templates'); // 'my-templates', 'create-template', 'credits'
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    category: 'text',
    is_public: false,
    template_data: JSON.stringify({
      backgroundColor: '#ffffff',
      textColor: '#000000',
      borderColor: '#000000',
      borderRadius: '10px',
      logoUrl: ''
    }, null, 2)
  });
  const [templateImage, setTemplateImage] = useState(null);
  
  // 템플릿 목록 및 크레딧 정보 로딩
  useEffect(() => {
    if (!user || !user.email) {
      setError(language === 'KR' ? '템플릿을 관리하려면 로그인이 필요합니다.' : 'Please log in to manage templates.');
      setLoading(false);
      return;
    }
    
    const fetchData = async () => {
      setLoading(true);
      try {
        // 사용자의 템플릿 목록 가져오기
        const templatesResponse = await axios.get(`${SERVER_ROOT}/api/qr/templates`, {
          params: { creator_email: user.email }
        });
        
        setTemplates(templatesResponse.data.templates || []);
        
        // 크레딧 정보 가져오기
        const creditsResponse = await axios.get(`${SERVER_ROOT}/api/qr/credits`, {
          params: { creator_email: user.email }
        });
        
        setCredits(creditsResponse.data || { summary: { total_credits: 0 } });
      } catch (error) {
        console.error('템플릿 데이터 로딩 오류:', error);
        setError(language === 'KR' ? '템플릿 정보를 불러오는 중 오류가 발생했습니다.' : 'Error loading template data.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [SERVER_ROOT, language, user]);
  
  // 템플릿 폼 입력 처리
  const handleTemplateFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTemplateForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // 템플릿 이미지 변경 처리
  const handleTemplateImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTemplateImage(file);
    }
  };
  
  // 템플릿 JSON 데이터 변경 처리
  const handleTemplateDataChange = (e) => {
    try {
      // 유효한 JSON 형식인지 검증
      JSON.parse(e.target.value);
      setTemplateForm(prev => ({
        ...prev,
        template_data: e.target.value
      }));
    } catch (error) {
      // 유효하지 않은 JSON이라면 에러를 표시할 수 있지만, 사용자가 편집중일 수 있으므로 값 자체는 업데이트
      setTemplateForm(prev => ({
        ...prev,
        template_data: e.target.value
      }));
    }
  };
  
  // 템플릿 생성 처리
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    
    if (!user || !user.email) {
      alert(language === 'KR' ? '템플릿을 생성하려면 로그인이 필요합니다.' : 'Please log in to create templates.');
      return;
    }
    
    // 템플릿 JSON 데이터 검증
    try {
      JSON.parse(templateForm.template_data);
    } catch (error) {
      alert(language === 'KR' ? '템플릿 데이터가 유효한 JSON 형식이 아닙니다.' : 'Template data is not valid JSON.');
      return;
    }
    
    try {
      setLoading(true);
      
      // FormData 생성
      const formData = new FormData();
      formData.append('name', templateForm.name);
      formData.append('description', templateForm.description);
      formData.append('creator_email', user.email);
      formData.append('category', templateForm.category);
      formData.append('is_public', templateForm.is_public);
      formData.append('template_data', templateForm.template_data);
      
      if (templateImage) {
        formData.append('template_image', templateImage);
      }
      
      // 템플릿 생성 API 호출
      const response = await axios.post(`${SERVER_ROOT}/api/qr/templates/create`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data && response.data.templateId) {
        alert(language === 'KR' ? '템플릿이 성공적으로 생성되었습니다!' : 'Template created successfully!');
        
        // 템플릿 폼 초기화
        setTemplateForm({
          name: '',
          description: '',
          category: 'text',
          is_public: false,
          template_data: JSON.stringify({
            backgroundColor: '#ffffff',
            textColor: '#000000',
            borderColor: '#000000',
            borderRadius: '10px',
            logoUrl: ''
          }, null, 2)
        });
        setTemplateImage(null);
        
        // 템플릿 목록 다시 로딩
        const templatesResponse = await axios.get(`${SERVER_ROOT}/api/qr/templates`, {
          params: { creator_email: user.email }
        });
        
        setTemplates(templatesResponse.data.templates || []);
        
        // 탭을 내 템플릿으로 전환
        setActiveTab('my-templates');
      }
    } catch (error) {
      console.error('템플릿 생성 오류:', error);
      alert(language === 'KR' ? '템플릿 생성 중 오류가 발생했습니다.' : 'Error creating template.');
    } finally {
      setLoading(false);
    }
  };
  
  // 템플릿 삭제 처리
  const handleDeleteTemplate = async (templateId) => {
    if (!user || !user.email) {
      alert(language === 'KR' ? '템플릿을 삭제하려면 로그인이 필요합니다.' : 'Please log in to delete templates.');
      return;
    }
    
    if (!window.confirm(language === 'KR' ? '이 템플릿을 삭제하시겠습니까?' : 'Are you sure you want to delete this template?')) {
      return;
    }
    
    try {
      await axios.delete(`${SERVER_ROOT}/api/qr/templates/${templateId}`, {
        params: { creator_email: user.email }
      });
      
      alert(language === 'KR' ? '템플릿이 성공적으로 삭제되었습니다!' : 'Template deleted successfully!');
      
      // 템플릿 목록에서 삭제된 템플릿 제거
      setTemplates(prev => prev.filter(template => template.id !== templateId));
    } catch (error) {
      console.error('템플릿 삭제 오류:', error);
      alert(language === 'KR' ? '템플릿 삭제 중 오류가 발생했습니다.' : 'Error deleting template.');
    }
  };
  
  // 템플릿 공개/비공개 토글 처리
  const handleTogglePublic = async (templateId, isCurrentlyPublic) => {
    if (!user || !user.email) {
      alert(language === 'KR' ? '템플릿 설정을 변경하려면 로그인이 필요합니다.' : 'Please log in to change template settings.');
      return;
    }
    
    try {
      await axios.put(`${SERVER_ROOT}/api/qr/templates/${templateId}`, {
        creator_email: user.email,
        is_public: !isCurrentlyPublic
      });
      
      // 템플릿 목록 업데이트
      setTemplates(prev => prev.map(template => 
        template.id === templateId 
          ? { ...template, is_public: !isCurrentlyPublic } 
          : template
      ));
    } catch (error) {
      console.error('템플릿 공개 설정 변경 오류:', error);
      alert(language === 'KR' ? '템플릿 설정 변경 중 오류가 발생했습니다.' : 'Error changing template visibility.');
    }
  };
  
  // 로딩 화면
  if (loading && activeTab !== 'create-template') {
    return (
      <div className="qr-template-manager">
        <div className="qr-loading">
          <div className="qr-spinner"></div>
          <p>{language === 'KR' ? '템플릿 정보 로딩 중...' : 'Loading template data...'}</p>
        </div>
      </div>
    );
  }
  
  // 오류 화면
  if (error && activeTab !== 'create-template') {
    return (
      <div className="qr-template-manager">
        <div className="qr-error">
          <p>{error}</p>
          {!user || !user.email ? (
            <button className="qr-btn">
              {language === 'KR' ? '로그인 페이지로 이동' : 'Go to Login Page'}
            </button>
          ) : (
            <button
              onClick={() => window.location.reload()}
              className="qr-btn"
            >
              {language === 'KR' ? '다시 시도' : 'Try Again'}
            </button>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="qr-template-manager">
      <div className="qr-template-tabs">
        <button
          className={`qr-tab ${activeTab === 'my-templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('my-templates')}
        >
          {language === 'KR' ? '내 템플릿' : 'My Templates'}
        </button>
        <button
          className={`qr-tab ${activeTab === 'create-template' ? 'active' : ''}`}
          onClick={() => setActiveTab('create-template')}
        >
          {language === 'KR' ? '새 템플릿 만들기' : 'Create New Template'}
        </button>
        <button
          className={`qr-tab ${activeTab === 'credits' ? 'active' : ''}`}
          onClick={() => setActiveTab('credits')}
        >
          {language === 'KR' ? '크레딧 현황' : 'Credits'}
        </button>
      </div>
      
      {/* 내 템플릿 목록 */}
      {activeTab === 'my-templates' && (
        <div className="qr-my-templates">
          <h2>{language === 'KR' ? '내 템플릿' : 'My Templates'}</h2>
          
          {templates.length === 0 ? (
            <div className="qr-no-templates">
              <p>{language === 'KR' ? '생성한 템플릿이 없습니다.' : 'You have not created any templates yet.'}</p>
              <button
                onClick={() => setActiveTab('create-template')}
                className="qr-btn"
              >
                {language === 'KR' ? '새 템플릿 만들기' : 'Create New Template'}
              </button>
            </div>
          ) : (
            <div className="qr-template-grid">
              {templates.map(template => (
                <div key={template.id} className="qr-template-card">
                  <div className="qr-template-header">
                    <h3>{template.name}</h3>
                    <span className={`qr-template-status ${template.is_public ? 'public' : 'private'}`}>
                      {template.is_public ? 
                        (language === 'KR' ? '공개' : 'Public') : 
                        (language === 'KR' ? '비공개' : 'Private')}
                    </span>
                  </div>
                  
                  <p className="qr-template-description">
                    {template.description || (language === 'KR' ? '설명 없음' : 'No description')}
                  </p>
                  
                  <div className="qr-template-details">
                    <p>
                      {language === 'KR' ? '카테고리' : 'Category'}: 
                      {template.category === 'text' ? (language === 'KR' ? ' 텍스트' : ' Text') : 
                       template.category === 'url' ? (language === 'KR' ? ' URL' : ' URL') :
                       template.category === 'vcard' ? (language === 'KR' ? ' 명함' : ' Contact') :
                       template.category === 'custom' ? (language === 'KR' ? ' 사용자 정의' : ' Custom') :
                       ` ${template.category}`}
                    </p>
                    <p>
                      {language === 'KR' ? '사용 횟수' : 'Usage Count'}: {template.use_count || 0}
                    </p>
                  </div>
                  
                  <div className="qr-template-actions">
                    <button
                      onClick={() => handleTogglePublic(template.id, template.is_public)}
                      className="qr-btn qr-btn-secondary"
                    >
                      {template.is_public ? 
                        (language === 'KR' ? '비공개로 전환' : 'Make Private') : 
                        (language === 'KR' ? '공개로 전환' : 'Make Public')}
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(template.id)}
                      className="qr-btn qr-btn-danger"
                    >
                      {language === 'KR' ? '삭제' : 'Delete'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* 새 템플릿 생성 */}
      {activeTab === 'create-template' && (
        <div className="qr-create-template">
          <h2>{language === 'KR' ? '새 템플릿 만들기' : 'Create New Template'}</h2>
          
          <form onSubmit={handleCreateTemplate} className="qr-template-form">
            <div className="qr-form-group">
              <label htmlFor="name">
                {language === 'KR' ? '템플릿 이름' : 'Template Name'}:
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={templateForm.name}
                onChange={handleTemplateFormChange}
                required
                placeholder={language === 'KR' ? '템플릿 이름 입력' : 'Enter template name'}
              />
            </div>
            
            <div className="qr-form-group">
              <label htmlFor="description">
                {language === 'KR' ? '설명' : 'Description'}:
              </label>
              <textarea
                id="description"
                name="description"
                value={templateForm.description}
                onChange={handleTemplateFormChange}
                placeholder={language === 'KR' ? '템플릿 설명 입력 (선택사항)' : 'Enter template description (optional)'}
                rows={3}
              />
            </div>
            
            <div className="qr-form-group">
              <label htmlFor="category">
                {language === 'KR' ? '카테고리' : 'Category'}:
              </label>
              <select
                id="category"
                name="category"
                value={templateForm.category}
                onChange={handleTemplateFormChange}
                required
              >
                <option value="text">{language === 'KR' ? '텍스트' : 'Text'}</option>
                <option value="url">{language === 'KR' ? 'URL' : 'URL'}</option>
                <option value="vcard">{language === 'KR' ? '명함' : 'Contact'}</option>
                <option value="custom">{language === 'KR' ? '사용자 정의' : 'Custom'}</option>
              </select>
            </div>
            
            <div className="qr-form-group qr-checkbox-group">
              <label htmlFor="is_public" className="qr-checkbox-label">
                <input
                  type="checkbox"
                  id="is_public"
                  name="is_public"
                  checked={templateForm.is_public}
                  onChange={handleTemplateFormChange}
                />
                <span>
                  {language === 'KR' ? '이 템플릿을 공개합니다 (다른 사용자가 사용 가능)' : 'Make this template public (available to other users)'}
                </span>
              </label>
            </div>
            <div className="qr-form-group">
              <label htmlFor="template_image">
                {language === 'KR' ? '템플릿 이미지 (선택사항)' : 'Template Image (optional)'}:
              </label>
              <input
                type="file"
                id="template_image"
                name="template_image"
                onChange={handleTemplateImageChange}
                accept="image/*"
              />
              <small>
                {language === 'KR' 
                  ? '템플릿에 사용될 이미지를 업로드하세요 (최대 크기: 2MB)' 
                  : 'Upload an image to use in your template (max size: 2MB)'}
              </small>
            </div>
            
            <div className="qr-form-group">
              <label htmlFor="template_data">
                {language === 'KR' ? '템플릿 데이터 (JSON)' : 'Template Data (JSON)'}:
              </label>
              <textarea
                id="template_data"
                name="template_data"
                value={templateForm.template_data}
                onChange={handleTemplateDataChange}
                rows={10}
                className="qr-code-editor"
                placeholder="{}"
                required
              />
              <small>
                {language === 'KR' 
                  ? '템플릿의 디자인 및 레이아웃 설정을 위한 JSON 데이터' 
                  : 'JSON data for template design and layout settings'}
              </small>
            </div>
            
            <div className="qr-form-actions">
              <button
                type="submit"
                className="qr-btn"
                disabled={loading}
              >
                {loading 
                  ? (language === 'KR' ? '생성 중...' : 'Creating...') 
                  : (language === 'KR' ? '템플릿 생성' : 'Create Template')}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('my-templates')}
                className="qr-btn qr-btn-secondary"
              >
                {language === 'KR' ? '취소' : 'Cancel'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* 크레딧 현황 */}
      {activeTab === 'credits' && (
        <div className="qr-credits">
          <h2>{language === 'KR' ? '크레딧 현황' : 'Credits Summary'}</h2>
          
          {!credits ? (
            <div className="qr-loading">
              <div className="qr-spinner"></div>
              <p>{language === 'KR' ? '크레딧 정보 로딩 중...' : 'Loading credit information...'}</p>
            </div>
          ) : (
            <div className="qr-credits-content">
              <div className="qr-credits-summary">
                <div className="qr-credit-stat">
                  <h3>{language === 'KR' ? '총 획득 크레딧' : 'Total Credits'}</h3>
                  <p className="qr-credit-value">{credits.summary?.total_credits || 0}</p>
                </div>
                <div className="qr-credit-stat">
                  <h3>{language === 'KR' ? '사용된 템플릿 수' : 'Templates Used'}</h3>
                  <p className="qr-credit-value">{credits.summary?.total_templates || 0}</p>
                </div>
                <div className="qr-credit-stat">
                  <h3>{language === 'KR' ? '사용자 수' : 'Unique Users'}</h3>
                  <p className="qr-credit-value">{credits.summary?.unique_users || 0}</p>
                </div>
              </div>
              
              {/* 템플릿별 크레딧 */}
              {credits.by_template && credits.by_template.length > 0 && (
                <div className="qr-credits-by-template">
                  <h3>{language === 'KR' ? '템플릿별 크레딧' : 'Credits by Template'}</h3>
                  <table className="qr-credits-table">
                    <thead>
                      <tr>
                        <th>{language === 'KR' ? '템플릿 이름' : 'Template Name'}</th>
                        <th>{language === 'KR' ? '카테고리' : 'Category'}</th>
                        <th>{language === 'KR' ? '크레딧' : 'Credits'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {credits.by_template.map((item, index) => (
                        <tr key={index}>
                          <td>{item.template_name}</td>
                          <td>{item.category}</td>
                          <td>{item.credit_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* 최근 크레딧 내역 */}
              {credits.recent && credits.recent.length > 0 && (
                <div className="qr-recent-credits">
                  <h3>{language === 'KR' ? '최근 크레딧 내역' : 'Recent Credits'}</h3>
                  <table className="qr-credits-table">
                    <thead>
                      <tr>
                        <th>{language === 'KR' ? '템플릿 이름' : 'Template Name'}</th>
                        <th>{language === 'KR' ? '사용자' : 'User'}</th>
                        <th>{language === 'KR' ? '날짜' : 'Date'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {credits.recent.map((item, index) => (
                        <tr key={index}>
                          <td>{item.template_name}</td>
                          <td>{item.user_email}</td>
                          <td>{new Date(item.created_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* 크레딧이 없는 경우 */}
              {credits.summary?.total_credits === 0 && (
                <div className="qr-no-credits">
                  <p>
                    {language === 'KR' 
                      ? '아직 획득한 크레딧이 없습니다. 다른 사용자들이 당신의 템플릿을 사용하면 크레딧이 적립됩니다.' 
                      : 'You have not earned any credits yet. Credits will be awarded when other users use your templates.'}
                  </p>
                  <button
                    onClick={() => setActiveTab('create-template')}
                    className="qr-btn"
                  >
                    {language === 'KR' ? '새 템플릿 만들기' : 'Create New Template'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QRTemplateManager;